import { Injectable, inject, resource, computed } from '@angular/core';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { FUNCTIONS_TOKEN } from '../firebase/functions.provider';
import { httpsCallable } from 'firebase/functions';
import { AuthService } from '../auth/auth.service';
import { AccessLogService } from './access-log.service';
import type { Account } from '../../shared/interfaces/account.interface';
import type { UserRole } from '../auth/role.model';

export interface CreateAccountPayload {
  email: string;
  password: string;
  role: UserRole;
  organizationId?: string;
  departmentId?: string;
  siteId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly functions = inject(FUNCTIONS_TOKEN);
  private readonly accessLogService = inject(AccessLogService);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private readonly accountResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole()
    }),
    loader: async ({ params: { orgId, role } }) => {
      if (role !== 'super_admin' && !orgId) {
        return [];
      }

      try {
        const accountsRef = collection(this.firestore, 'accounts');
        let accountsQuery;
        
        if (role === 'super_admin') {
          accountsQuery = query(
            accountsRef,
            where('deletedAt', '==', null)
          );
        } else {
          accountsQuery = query(
            accountsRef,
            where('organizationId', '==', orgId),
            where('deletedAt', '==', null)
          );
        }
        
        const snapshot = await getDocs(accountsQuery);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Account));
      } catch (err) {
        console.error('アカウント一覧の取得に失敗しました:', err);
        throw new Error('アカウント一覧の取得に失敗しました', { cause: err });
      }
    }
  });

  public readonly accounts = computed(() => this.accountResource.value() ?? []);
  
  public readonly isLoading = computed(() => 
    this.accountResource.status() === 'loading' || 
    this.accountResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const err = this.accountResource.error();
    return err instanceof Error ? err.message : null;
  });

  public async createAccount(payload: CreateAccountPayload): Promise<void> {
    const createAccountFn = httpsCallable<CreateAccountPayload, { accountId: string } | void>(this.functions, 'createAccount');
    let status: 'success' | 'error' = 'success';
    let accountId = '';
    try {
      const result = await createAccountFn(payload);
      // createAccount function currently might not return accountId. If it does, we can use it.
      // We will just log 'accounts' for now or maybe result.data.accountId if returned.
      accountId = (result.data as any)?.accountId || '';
    } catch (err) {
      status = 'error';
      throw err;
    } finally {
      await this.logAction('account.create', accountId ? `accounts/${accountId}` : 'accounts', status);
    }
  }


  public async updateAccount(accountId: string, displayName?: string, password?: string): Promise<void> {
    const updateAccountFn = httpsCallable<{ accountId: string; displayName?: string; password?: string }, void>(this.functions, 'updateAccount');
    await updateAccountFn({
      accountId,
      ...(displayName !== undefined ? { displayName } : {}),
      // password requires Truthy value (cannot be empty string)
      ...(password ? { password } : {}),
    });
  }

  public async deleteAccount(accountId: string): Promise<void> {
    const deleteAccountFn = httpsCallable<{ accountId: string }, void>(this.functions, 'deleteAccount');
    let status: 'success' | 'error' = 'success';
    try {
      await deleteAccountFn({ accountId });
    } catch (err) {
      status = 'error';
      throw err;
    } finally {
      await this.logAction('account.delete', `accounts/${accountId}`, status);
    }
  }
  
  public reload(): void {
    this.accountResource.reload();
  }
}
